// Shared media-URL detection and inbox preview-labeling logic.
// Used by both the messages page and the dashboard inbox panels so the two
// stay in sync when new media types are added (Task #2481).

import { HTML_IMG_REGEX, STRIP_TAGS_REGEX } from "@shared/inbox-preview";

export const URL_REGEX = /https?:\/\/[^\s)]+/gi;
export const MEDIA_URL_REGEX = /\.(?:jpe?g|png|gif|webp|heic|heif|pdf)(?:[?#]|$)/i;
export const VIDEO_URL_REGEX = /\.(?:mp4|mov|webm|avi|mkv|m4v|3gp)(?:[?#]|$)/i;
export const AUDIO_URL_REGEX = /\.(?:mp3|m4a|ogg|oga|wav|aac|amr|opus|weba)(?:[?#]|$)/i;
export const TWILIO_MEDIA_REGEX = /\.twilio\.com\/.+\/Media\//i;

// Re-export the shared HTML <img> matcher so existing importers of
// `HTML_IMG_REGEX` from this module keep working; the regex (and the
// tag-stripping logic below) now live in `@shared/inbox-preview` as the single
// source of truth shared with the server-side preview compute path (Task #3200).
export { HTML_IMG_REGEX };

export function mediaTypeForUrl(url: string): "image" | "video" | "audio" | null {
  if (VIDEO_URL_REGEX.test(url)) return "video";
  if (AUDIO_URL_REGEX.test(url)) return "audio";
  if (MEDIA_URL_REGEX.test(url) || TWILIO_MEDIA_REGEX.test(url)) return "image";
  return null;
}

export function looksLikeMediaUrl(url: string): boolean {
  return mediaTypeForUrl(url) !== null;
}

export function extractMediaUrlsFromText(text: string | null | undefined): string[] {
  if (!text) return [];
  const matches = text.match(URL_REGEX);
  if (!matches) return [];
  return matches.map((m) => m.replace(/[.,;]+$/, "")).filter(looksLikeMediaUrl);
}

export function mediaLabelFor(urls: string[]): string {
  const types = new Set(urls.map(mediaTypeForUrl));
  if (types.size === 1) {
    const [type] = [...types];
    if (type === "video") return "Video";
    if (type === "audio") return "Audio";
    return "Image";
  }
  return "Media";
}

export function formatInboxPreview(
  preview: string | null | undefined,
  emptyFallback = "",
): string {
  if (!preview) return emptyFallback;

  // Handle HTML img tags delivered as raw markup by PadSplit.
  if (HTML_IMG_REGEX.test(preview)) {
    const textOnly = preview.replace(STRIP_TAGS_REGEX, "").trim();
    return textOnly.length === 0 ? "Image" : textOnly;
  }

  const mediaUrls = extractMediaUrlsFromText(preview);
  if (mediaUrls.length === 0) return preview;
  const stripped = mediaUrls.reduce((acc, url) => acc.replace(url, ""), preview).trim();
  return stripped.length === 0 ? mediaLabelFor(mediaUrls) : preview;
}

// Returns the media type for a media-only inbox preview (one whose text is
// nothing but media URLs), or null when the preview has real text. Drives the
// small leading icon next to the "Image"/"Video"/"Audio"/"Media" label.
export function inboxPreviewMediaType(
  preview: string | null | undefined,
): "image" | "video" | "audio" | "media" | null {
  if (!preview) return null;

  // Handle HTML img tags: img-only → "image"; img + text → null (text shown, no icon).
  if (HTML_IMG_REGEX.test(preview)) {
    const textOnly = preview.replace(STRIP_TAGS_REGEX, "").trim();
    return textOnly.length === 0 ? "image" : null;
  }

  const mediaUrls = extractMediaUrlsFromText(preview);
  if (mediaUrls.length === 0) return null;
  const stripped = mediaUrls.reduce((acc, url) => acc.replace(url, ""), preview).trim();
  if (stripped.length !== 0) return null;
  const types = new Set(mediaUrls.map(mediaTypeForUrl));
  if (types.size === 1) {
    const [type] = [...types];
    return type ?? "media";
  }
  return "media";
}
